// src/components/Sections/CaseStudiesSection.tsx
import React, { useState } from 'react';
import {
  Calendar,
  Users,
  Shield,
  Database,
  BookOpen,
  Zap,
  Server,
  GitBranch,
  ExternalLink,
  ChevronRight,
  X,
  CheckCircle,
  Target,
  Layers
} from 'lucide-react';
import { useAppContext } from '../../contexts/AppContext';

interface CaseStudy {
  id: string;
  title: string;
  client: string;
  period: string;
  role: string;
  description: string;
  achievements: string[];
  technologies: string[];
  category: 'fullstack' | 'backend' | 'architecture' | 'author';
  featured: boolean;
  icon: any;
  color: string;
  challenge?: string;
  solution?: string;
  results?: string[];
  demoUrl?: string;
  githubUrl?: string;
  link?: string;
}

const CaseStudiesSection: React.FC = () => {
  const { state } = useAppContext();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedCaseStudy, setSelectedCaseStudy] = useState<CaseStudy | null>(null);

  // Translation helper
  const t = (sv: string, en: string) => state.language === 'sv' ? sv : en;

  const caseStudies: CaseStudy[] = [
    {
      id: 'skandinaviska-musikskolan',
      title: t('Schemaläggningssystem för musiklektioner', 'Scheduling System for Music Lessons'),
      client: 'Skandinaviska Musikskolan',
      period: t('2026-05 - pågående', '2026-05 - ongoing'),
      role: t(
        'Senior Fullstack-utvecklare / Lösningsarkitekt',
        'Senior Fullstack Developer / Solution Architect'
      ),
      description: t(
        'Utvecklar schemaläggningssystem för bokning av musiklektioner och hantering av lärarschema åt Skandinaviska Musikskolan. Bygger en modern lösning med fokus på enkel bokning för elever och tydlig överblick för lärare.',
        'Developing a scheduling system for booking music lessons and managing teacher schedules for Skandinaviska Musikskolan. Building a modern solution focused on easy booking for students and a clear schedule overview for teachers.'
      ),
      achievements: [
        t('Bygger boknings- och schemaläggningssystem från grunden', 'Building booking and scheduling system from the ground up'),
        t('Designar databasmodell för lärare, elever och lektioner', 'Designing database model for teachers, students and lessons'),
        t('Utvecklar responsiv React-frontend', 'Developing responsive React frontend')
      ],
      technologies: [
        '.NET Core', 'React', 'TypeScript', 'SQL Server', 'Tailwind CSS'
      ],
      category: 'fullstack',
      featured: true,
      icon: Zap,
      color: 'yellow',
      demoUrl: 'https://www.musikschema.se',
      link: 'https://www.musikschema.se'
    },
    {
      id: 'ambulance-scheduling',
      title: t('Schemaläggningsapp för ambulanspersonal', 'Scheduling App for Ambulance Personnel'),
      client: 'Ambulanser i Stor-Stockholm AB',
      period: '2025-05 - 2026-04',
      role: t('Full Stack-utvecklare', 'Full Stack Developer'),
      description: t(
        'Konverterade och förbättrade schemaläggningsapplikation från PHP till modern stack. Migrerade till .NET Core backend med React/Next.js frontend.',
        'Converted and improved scheduling application from PHP to modern stack. Migrated to .NET Core backend with React/Next.js frontend.'
      ),
      achievements: [
        t('Migrerade från PHP till .NET Core', 'Migrated from PHP to .NET Core'),
        t('Byggde responsiv React-frontend', 'Built responsive React frontend'),
        t('Utvecklade parallell Next.js-version', 'Developed parallel Next.js version')
      ],
      technologies: [
        '.NET Core', 'React', 'Next.js', 'Tailwind CSS', 'TypeScript', 'SQL Server'
      ],
      category: 'fullstack',
      featured: true,
      icon: Users,
      color: 'indigo',
      demoUrl: 'https://ambuschema.se',
      link: 'https://ambuschema.se'  // For clickable title in modal
    },
    {
      id: 'technical-book',
      title: 'The Aspiring Full Stack Developer\'s Playbook',
      client: t('Egen publikation', 'Own publication'),
      period: '2024-07 - 2024-12',
      role: t('Författare & Utvecklare', 'Author & Developer'),
      description: t(
        'Skrev och publicerade omfattande teknisk bok om modern fullstack-utveckling. Utvecklade 12 tillhörande applikationer som praktiska exempel på GitHub.',
        'Wrote and published comprehensive technical book on modern fullstack development. Developed 12 accompanying applications as practical examples on GitHub.'
      ),
      achievements: [
        t('Publicerade bok om Full Stack-utveckling', 'Published book on Full Stack development'),
        t('Utvecklade 12 demo-applikationer', 'Developed 12 demo applications'),
        t('Täckte Docker, Kubernetes, mikrotjänster', 'Covered Docker, Kubernetes, microservices')
      ],
      technologies: [
        '.NET Core', 'Docker', 'Kubernetes', 'React', 'Angular', 'Vue',
        'Blazor', 'ActiveMQ', 'RabbitMQ', 'Kafka'
      ],
      category: 'author',
      featured: true,
      icon: BookOpen,
      color: 'purple',
      demoUrl: 'https://a.co/d/aQaJaBx',
      githubUrl: 'https://github.com/developersplaybook'
    },
    {
      id: 'svenska-kraftnat',
      title: t('Flödesbaserade kapacitetsberäkningar', 'Flow-based Capacity Calculations'),
      client: 'Svenska Kraftnät',
      period: '2022-09 - 2024-06',
      role: t(
        'Senior Fullstack-utvecklare / Lösningsarkitekt / Lead Developer',
        'Senior Fullstack Developer / Solution Architect / Lead Developer'
      ),
      description: t(
        'Huvudansvarig för design och implementering av två övervakningsapplikationer: FileMonitor (SignalR-baserad webbapp) och FileArchiver (Windows-tjänst). Införde AWS S3 Buckets för dokumentlagring och implementerade omfattande säkerhetslösningar.',
        'Lead responsible for design and implementation of two monitoring applications: FileMonitor (SignalR-based web app) and FileArchiver (Windows service). Introduced AWS S3 Buckets for document storage and implemented comprehensive security solutions.'
      ),
      challenge: t(
        'Svenska Kraftnät behövde ett robust system för att övervaka och hantera flödesbaserade kapacitetsberäkningar i realtid, som kunde hantera stora datamängder från både Linux- och Windows-miljöer.',
        'Svenska Kraftnät needed a robust system to monitor and manage flow-based capacity calculations in real-time that could handle large amounts of data from both Linux and Windows environments.'
      ),
      solution: t(
        'Utvecklade FileMonitor för realtidsövervakning med SignalR och FileArchiver för automatiserad filhantering. Implementerade AWS S3 Buckets och byggde robusta CI/CD-pipelines.',
        'Developed FileMonitor for real-time monitoring with SignalR and FileArchiver for automated file management. Implemented AWS S3 Buckets and built robust CI/CD pipelines.'
      ),
      achievements: [
        t('Lead Developer för två nya övervakningsapplikationer', 'Lead Developer for two new monitoring applications'),
        t('Implementerade AWS S3 Buckets på NetApp-servrar', 'Implemented AWS S3 Buckets on NetApp servers'),
        t('Byggde realtidsvisualisering med SignalR', 'Built real-time visualization with SignalR'),
        t('Ansvarade för CI/CD pipelines i Azure DevOps', 'Responsible for CI/CD pipelines in Azure DevOps')
      ],
      results: [
        t('Minskade manuell övervakning med 90%', 'Reduced manual monitoring by 90%'),
        t('Förbättrade systemets responstid med 60%', 'Improved system response time by 60%'),
        t('Noll säkerhetsincidenter sedan lansering', 'Zero security incidents since launch')
      ],
      technologies: [
        'ASP.NET 7/8', 'SignalR', 'SQL Server', 'Entity Framework Core',
        'AWS S3', 'Azure DevOps', 'Docker', 'Windows Server', 'Linux'
      ],
      category: 'architecture',
      featured: true,
      icon: Zap,
      color: 'yellow'
    },
    {
      id: 'svea-bank',
      title: t('AML/KYC System', 'AML/KYC System'),
      client: 'Svea Bank',
      period: '2021-09 - 2022-07',
      role: t('Senior Fullstack-utvecklare', 'Senior Fullstack Developer'),
      description: t(
        'Del av agilt team som nyutvecklade system för Anti Money Laundering och Know Your Customer. Designade mikrotjänstarkitektur med domändriven design och byggde robust köhanteringssystem.',
        'Part of agile team developing new system for Anti Money Laundering and Know Your Customer. Designed microservice architecture with domain-driven design and built robust queue management system.'
      ),
      challenge: t(
        'Svea Bank behövde ett modernt och säkert system för att hantera regelverket kring anti-penningtvätt och kundkännedom.',
        'Svea Bank needed a modern and secure system to handle anti-money laundering and customer due diligence regulations.'
      ),
      solution: t(
        'Utvecklade en händelsedriven mikrotjänstarkitektur med fem separata tjänster. Implementerade custom köhanterare när NServiceBus inte motsvarade kraven.',
        'Developed an event-driven microservice architecture with five separate services. Implemented custom queue manager when NServiceBus didn\'t meet requirements.'
      ),
      achievements: [
        t('Arkitekterade system med 5 mikrotjänster', 'Architected system with 5 microservices'),
        t('Utvecklade custom köhanterare med SQL Server', 'Developed custom queue manager with SQL Server'),
        t('Implementerade omfattande integrationstester', 'Implemented comprehensive integration tests'),
        t('Byggde realtidsnotifieringar med SignalR', 'Built real-time notifications with SignalR')
      ],
      results: [
        t('Minskade handläggningstiden med 70%', 'Reduced processing time by 70%'),
        t('Automatiserade 80% av AML-kontrollen', 'Automated 80% of AML controls')
      ],
      technologies: [
        'ASP.NET 6', 'C# & F#', 'React', 'TypeScript', 'Recoil',
        'SignalR', 'SQL Server', 'OAuth 2.0', 'JWT', 'AWS S3'
      ],
      category: 'fullstack',
      featured: true,
      icon: Shield,
      color: 'green'
    },
    {
      id: 'finansinspektionen',
      title: 'Enhanced Data Handling (EDH)',
      client: 'Finansinspektionen',
      period: '2020-07 - 2021-06',
      role: t('Senior Fullstack-utvecklare', 'Senior Fullstack Developer'),
      description: t(
        'Nyutveckling av omfattande monolitisk datahanteringsapplikation med Vue.js frontend och .NET Core backend. Implementerade ElasticSearch för avancerad sökning och hantering av stora datamängder.',
        'Development of comprehensive monolithic data management application with Vue.js frontend and .NET Core backend. Implemented ElasticSearch for advanced search and handling of large data sets.'
      ),
      achievements: [
        t('Implementerade ElasticSearch med NEST', 'Implemented ElasticSearch with NEST'),
        t('Byggde robust monolitisk arkitektur', 'Built robust monolithic architecture'),
        t('Utvecklade omfattande REST API', 'Developed comprehensive REST API')
      ],
      technologies: [
        'Vue.js', 'Vuex', 'TypeScript', 'ASP.NET Core', 'ElasticSearch',
        'SQL Server', 'Entity Framework Core', 'Hangfire'
      ],
      category: 'fullstack',
      featured: false,
      icon: Database,
      color: 'blue'
    }
  ];

  const categories = [
    { id: 'all', label: t('Alla projekt', 'All projects'), count: caseStudies.length },
    { id: 'fullstack', label: 'Full Stack', count: caseStudies.filter(cs => cs.category === 'fullstack').length },
    { id: 'architecture', label: t('Arkitektur', 'Architecture'), count: caseStudies.filter(cs => cs.category === 'architecture').length },
    { id: 'author', label: t('Publikationer', 'Publications'), count: caseStudies.filter(cs => cs.category === 'author').length }
  ];

  const filteredCaseStudies = selectedCategory === 'all' 
    ? caseStudies 
    : caseStudies.filter(cs => cs.category === selectedCategory);

  const getColorClasses = (color: string) => {
    const colors: { [key: string]: { bg: string; text: string; border: string } } = {
      blue: {
        bg: 'bg-blue-100 dark:bg-blue-900/30',
        text: 'text-blue-600 dark:text-blue-400',
        border: 'border-blue-200 dark:border-blue-800'
      },
      green: {
        bg: 'bg-green-100 dark:bg-green-900/30',
        text: 'text-green-600 dark:text-green-400',
        border: 'border-green-200 dark:border-green-800'
      },
      purple: {
        bg: 'bg-purple-100 dark:bg-purple-900/30',
        text: 'text-purple-600 dark:text-purple-400',
        border: 'border-purple-200 dark:border-purple-800'
      },
      yellow: {
        bg: 'bg-yellow-100 dark:bg-yellow-900/30',
        text: 'text-yellow-600 dark:text-yellow-400',
        border: 'border-yellow-200 dark:border-yellow-800'
      },
      indigo: {
        bg: 'bg-indigo-100 dark:bg-indigo-900/30',
        text: 'text-indigo-600 dark:text-indigo-400',
        border: 'border-indigo-200 dark:border-indigo-800'
      }
    };
    return colors[color] || colors.blue;
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    element?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <section id="case-studies" className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center px-4 py-2 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-6">
              <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                Portfolio
              </span>
            </div>
            
            <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
              {t('Tidigare uppdrag & projekt', 'Previous assignments & projects')}
            </h2>
            
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
              {t(
                'Ett urval av projekt från mina senaste uppdrag inom bank, myndighet och kraftnät',
                'A selection of projects from my recent assignments in banking, government and energy sectors'
              )}
            </p>
          </div>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3 mb-12">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-4 py-2 rounded-full font-medium transition-all duration-200 ${
                  selectedCategory === category.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                {category.label}
                <span className="ml-2 text-sm opacity-75">({category.count})</span>
              </button>
            ))}
          </div>

          {/* Case Studies Grid */}
          <div className="grid md:grid-cols-2 gap-8">
            {filteredCaseStudies.map((study) => {
              const Icon = study.icon;
              const colors = getColorClasses(study.color);
              
              return (
                <div
                  key={study.id}
                  className={`bg-white dark:bg-gray-900 rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 border ${colors.border} cursor-pointer ${
                    study.featured ? 'md:col-span-2 lg:col-span-1' : ''
                  }`}
                  onClick={() => setSelectedCaseStudy(study)}
                >
                  {/* Header */}
                  <div className={`p-6 ${colors.bg}`}>
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-12 h-12 ${colors.bg} rounded-lg flex items-center justify-center border-2 ${colors.border}`}>
                        <Icon className={`h-6 w-6 ${colors.text}`} />
                      </div>
                      {study.featured && (
                        <span className="px-3 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded-full text-xs font-semibold">
                          Featured
                        </span>
                      )}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      {study.title}
                    </h3>
                    <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {study.client}
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {study.period}
                      </span>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6">
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      {study.description}
                    </p>

                    <div className="mb-4">
                      <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                        {t('Roll', 'Role')}: 
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {study.role}
                      </p>
                    </div>

                    {/* Technologies */}
                    <div className="mb-4">
                      <div className="flex flex-wrap gap-2">
                        {study.technologies.slice(0, 5).map((tech) => (
                          <span
                            key={tech}
                            className="px-2 py-1 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded text-xs font-medium"
                          >
                            {tech}
                          </span>
                        ))}
                        {study.technologies.length > 5 && (
                          <span className="px-2 py-1 text-gray-500 dark:text-gray-500 text-xs">
                            +{study.technologies.length - 5} {t('mer', 'more')}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* External Links Section */}
                    <div className="flex items-center gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                        {t('Läs mer', 'Read more')} →
                      </span>
                      
                      {study.demoUrl && (
                        <a
                          href={study.demoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 text-sm font-medium transition-colors"
                        >
                          <Server className="mr-1 h-4 w-4" />
                          Demo
                        </a>
                      )}
                      
                      {study.githubUrl && (
                        <a
                          href={study.githubUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 text-sm font-medium transition-colors"
                        >
                          <GitBranch className="mr-1 h-4 w-4" />
                          GitHub
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div className="mt-16 text-center">
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {t(
                'Letar du efter en erfaren utvecklare för ditt nästa projekt?',
                'Looking for an experienced developer for your next project?'
              )}
            </p>
            <button
              onClick={scrollToContact}
              className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200"
            >
              {t('Låt oss diskutera ditt projekt', "Let's discuss your project")}
              <ChevronRight className="ml-2 h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Modal for Case Study Details */}
      {selectedCaseStudy && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white dark:bg-gray-900 rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 p-6">
              <div className="flex justify-between items-start">
                <div>
                  {selectedCaseStudy.link ? (
                    <a 
                      href={selectedCaseStudy.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="group inline-block"
                    >
                      <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors inline-flex items-center">
                        {selectedCaseStudy.title}
                        <ExternalLink className="ml-2 h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </h3>
                    </a>
                  ) : (
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                      {selectedCaseStudy.title}
                    </h3>
                  )}
                  <div className="flex items-center gap-4 text-sm text-gray-600 dark:text-gray-400">
                    <span>{selectedCaseStudy.client}</span>
                    <span>•</span>
                    <span>{selectedCaseStudy.period}</span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedCaseStudy(null)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <X className="h-6 w-6 text-gray-500 dark:text-gray-400" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Role */}
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
                  {t('Min roll', 'My role')}
                </h4>
                <p className="text-gray-600 dark:text-gray-400">
                  {selectedCaseStudy.role}
                </p>
              </div>

              {/* Challenge */}
              {selectedCaseStudy.challenge && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                    <Target className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
                    {t('Utmaning', 'Challenge')}
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    {selectedCaseStudy.challenge}
                  </p>
                </div>
              )}

              {/* Solution */}
              {selectedCaseStudy.solution && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                    <Layers className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
                    {t('Lösning', 'Solution')}
                  </h4>
                  <p className="text-gray-600 dark:text-gray-400">
                    {selectedCaseStudy.solution}
                  </p>
                </div>
              )}

              {/* Achievements */}
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                  <CheckCircle className="h-5 w-5 mr-2 text-blue-600 dark:text-blue-400" />
                  {t('Huvudsakliga bidrag', 'Key contributions')}
                </h4>
                <ul className="space-y-2">
                  {selectedCaseStudy.achievements.map((achievement, idx) => (
                    <li key={idx} className="flex items-start text-gray-600 dark:text-gray-400">
                      <span className="text-blue-600 dark:text-blue-400 mr-2 mt-1">•</span>
                      {achievement}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Results */}
              {selectedCaseStudy.results && (
                <div>
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
                    <CheckCircle className="h-5 w-5 mr-2 text-green-600 dark:text-green-400" />
                    {t('Resultat', 'Results')}
                  </h4>
                  <ul className="space-y-2">
                    {selectedCaseStudy.results.map((result, idx) => (
                      <li key={idx} className="flex items-start text-gray-600 dark:text-gray-400">
                        <span className="text-green-600 dark:text-green-400 mr-2 mt-1">✓</span>
                        {result}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Technologies */}
              <div>
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3">
                  {t('Teknologier', 'Technologies')}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedCaseStudy.technologies.map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-full text-sm font-medium"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* External Links in Modal */}
              {(selectedCaseStudy.demoUrl || selectedCaseStudy.githubUrl) && (
                <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-4">
                    {selectedCaseStudy.demoUrl && (
                      <a
                        href={selectedCaseStudy.demoUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                      >
                        <Server className="mr-2 h-4 w-4" />
                        {t('Visa Demo', 'View Demo')}
                      </a>
                    )}
                    
                    {selectedCaseStudy.githubUrl && (
                      <a
                        href={selectedCaseStudy.githubUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center px-4 py-2 bg-gray-800 hover:bg-gray-900 text-white rounded-lg transition-colors"
                      >
                        <GitBranch className="mr-2 h-4 w-4" />
                        GitHub
                      </a>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CaseStudiesSection;